// Leer un PDF con pdf.js — ADR-2026-09-08-el-agente-lee-y-crea-pdfs.
//
// Acá SÍ entra una librería, al revés que con el xlsx y que con la escritura de PDF. Un
// PDF guarda el texto como glifos posicionados, con streams comprimidos, codificaciones de
// fuente variadas y tablas ToUnicode. Un extractor casero anda con los PDFs de prueba y
// devuelve MOJIBAKE con la mitad de los reales: no falla, miente. `pdfjs-dist` es de
// Mozilla y es la implementación de referencia.
//
// Entra con `import()` DINÁMICO: pesa ~1MB y no tiene por qué estar en el bundle principal
// de una app donde la mayoría nunca adjunta un PDF.
//
// Dos clases de PDF, y la segunda es la mitad de los que la gente tiene:
//   • Con capa de texto (exportado de Word, Excel, un sistema de facturación) → se extrae.
//   • ESCANEADO o fotografiado (una factura del proveedor, un contrato firmado) → no tiene
//     texto, es una imagen adentro de un PDF. Ahí se RENDERIZAN las páginas y el agente las
//     mira como imágenes.

export interface PdfLeido {
  paginas: number;
  /** Texto por página, en orden. Vacío donde la página no tenía capa de texto. */
  textoPorPagina: string[];
  /** false = escaneado (o casi): hay que mirarlo como imagen. */
  tieneTexto: boolean;
}

/** Debajo de esto tratamos el PDF como escaneado: un par de sellos sueltos no es texto. */
const MIN_CARACTERES_PARA_SER_TEXTO = 40;

type Pdfjs = typeof import('pdfjs-dist');

let pdfjsCache: Promise<Pdfjs> | null = null;

/** Carga pdf.js una sola vez y le apunta el worker. */
function cargarPdfjs(): Promise<Pdfjs> {
  if (!pdfjsCache) {
    pdfjsCache = import('pdfjs-dist').then((mod) => {
      // El worker se resuelve por URL desde el propio paquete: así Vite lo emite como
      // asset aparte y no hay que copiarlo a mano a `public/`.
      mod.GlobalWorkerOptions.workerSrc = new URL(
        'pdfjs-dist/build/pdf.worker.min.mjs',
        import.meta.url,
      ).toString();
      return mod;
    });
  }
  return pdfjsCache;
}

async function abrir(bytes: Uint8Array) {
  const pdfjs = await cargarPdfjs();
  try {
    // `bytes.slice()`: pdf.js se queda con el buffer y lo deja detached, y el mismo
    // Uint8Array se vuelve a usar si el usuario pide renderizar después.
    return await pdfjs.getDocument({ data: bytes.slice() }).promise;
  } catch {
    throw new Error('No pude abrir ese PDF. ¿Está completo y sin contraseña?');
  }
}

/** Texto de todas las páginas (hasta `maxPaginas`). */
export async function leerPdf(bytes: Uint8Array, maxPaginas = 200): Promise<PdfLeido> {
  const doc = await abrir(bytes);
  const total = Math.min(doc.numPages, maxPaginas);
  const textoPorPagina: string[] = [];
  for (let i = 1; i <= total; i++) {
    try {
      const pagina = await doc.getPage(i);
      const contenido = await pagina.getTextContent();
      // Los items vienen como fragmentos posicionados; `hasEOL` marca el fin de renglón.
      const texto = contenido.items
        .map((it) => {
          const item = it as { str?: string; hasEOL?: boolean };
          return (item.str ?? '') + (item.hasEOL ? '\n' : '');
        })
        .join('')
        .replace(/[ \t]+/g, ' ')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
      textoPorPagina.push(texto);
    } catch {
      textoPorPagina.push('');
    }
  }
  const caracteres = textoPorPagina.reduce((n, t) => n + t.length, 0);
  return {
    paginas: doc.numPages,
    textoPorPagina,
    tieneTexto: caracteres >= MIN_CARACTERES_PARA_SER_TEXTO,
  };
}

/**
 * Renderiza páginas a data URL (JPEG). Es el camino de los ESCANEADOS: entran por los
 * bloques de imagen que el chat ya sabe mandar.
 *
 * `escala` acota el tamaño: una página A4 a escala 1.5 son ~1240px de ancho, suficiente
 * para que el modelo lea un texto impreso sin que el pedido pese de más.
 */
export async function renderizarPaginas(
  bytes: Uint8Array,
  desde: number,
  cantidad: number,
  escala = 1.5,
): Promise<string[]> {
  const doc = await abrir(bytes);
  const out: string[] = [];
  const primera = Math.max(1, Math.floor(desde));
  const ultima = Math.min(doc.numPages, primera + Math.max(1, Math.floor(cantidad)) - 1);
  for (let i = primera; i <= ultima; i++) {
    try {
      const pagina = await doc.getPage(i);
      const viewport = pagina.getViewport({ scale: escala });
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(viewport.width);
      canvas.height = Math.round(viewport.height);
      const ctx = canvas.getContext('2d');
      if (!ctx) continue;
      // Fondo blanco: un PDF sin fondo se renderiza transparente y al pasarlo a JPEG
      // queda negro.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      await pagina.render({ canvas, canvasContext: ctx, viewport }).promise;
      out.push(canvas.toDataURL('image/jpeg', 0.82));
    } catch {
      /* una página que no renderiza se saltea; el resto sirve igual */
    }
  }
  return out;
}
